// Daily Reports: parse the JSON an AI prepares from chat messages into work records.
// Accepts a JSON array (or { records: [...] }), optionally wrapped in ```json fences.
// Item keys are flexible: employee|name|employeeID, date|workday|day, shift (name or id),
// start|startTime|from, end|endTime|to, break|breakMinutes. Dates: YYYY-MM-DD or M/D/YYYY;
// times: "7:00", "07:00", "7 AM", "3:30 pm". Employees match by id, full name or a unique
// first name (case-insensitive). Returns { rows, errors }: rows carry `error` when invalid.

export const EXAMPLE_JSON = `[
  { "employee": "Clara", "date": "2026-09-01", "shift": "Morning", "start": "07:00", "end": "14:30", "break": 0 },
  { "employee": "Clara", "date": "2026-09-02", "shift": "Morning", "start": "07:00", "end": "14:30", "break": 30 },
  { "employee": "Vernon Padmore", "date": "09/03/2026", "start": "8:00 AM", "end": "4:00 PM" }
]`;

export const aiPrompt = (year, employeeNames, shiftNames) =>
`Convert the messages below into JSON for our work report. Output ONLY a JSON array, no comments.
One item per employee per worked day:
{ "employee": "<first name or full name>", "date": "YYYY-MM-DD", "shift": "<shift name>", "start": "HH:MM", "end": "HH:MM", "break": <minutes, 0 if none> }
Times in 24-hour format. If the year is not stated, use ${year}. Skip days off.
Known employees: ${employeeNames.join(', ')}.
Shifts: ${shiftNames.join(', ')} (default "${shiftNames[0] || 'Morning'}").

Messages:
`;

const pad2 = n => String(n).padStart(2, '0');

export function normDate(v) {
  if (v == null) return null;
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${pad2(m[2])}-${pad2(m[3])}`;
  m = s.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/);
  if (m) return `${m[3]}-${pad2(m[1])}-${pad2(m[2])}`;
  return null;
}

export function normTime(v) {
  if (v == null) return null;
  const m = String(v).trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?$/i);
  if (!m) return null;
  let h = Number(m[1]); const min = Number(m[2] || 0);
  const ap = (m[3] || '').toLowerCase().replace(/\./g, '');
  if (ap === 'pm' && h < 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  if (h > 23 || min > 59) return null;
  return `${pad2(h)}:${pad2(min)}`;
}

function stripFences(text) {
  const t = String(text || '').trim();
  const m = t.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  return m ? m[1] : t;
}

function findEmployee(item, employees) {
  const id = item.employeeID ?? item.employee_id ?? item.id;
  if (id != null && String(id).trim() !== '' && !isNaN(Number(id))) {
    const e = employees.find(x => String(x.employeeID) === String(id));
    if (e) return e;
  }
  const name = String(item.employee ?? item.name ?? item.employeeName ?? '').trim().toLowerCase();
  if (!name) return null;
  const full = employees.filter(x => nameOf(x).toLowerCase() === name);
  if (full.length === 1) return full[0];
  const first = employees.filter(x => nameOf(x).toLowerCase().split(/\s+/)[0] === name.split(/\s+/)[0]);
  return first.length === 1 ? first[0] : null;
}
// /api/work-reports/employees returns { employeeID, fullName }; other lists use employeeName
export const nameOf = e => String(e.fullName || e.employeeName || '').trim();

function findShift(item, shifts) {
  const v = item.shift ?? item.shift_id ?? item.shiftName;
  if (v == null || String(v).trim() === '') return shifts[0] || null;
  const s = String(v).trim().toLowerCase();
  return shifts.find(x => String(x.id) === s || String(x.name || '').toLowerCase() === s) || null;
}

export function parseWorkJson(text, employees, shifts) {
  const body = stripFences(text);
  if (!body) return { rows: [], errors: [] };
  let data;
  try { data = JSON.parse(body); } catch (e) { return { rows: [], errors: [`Not valid JSON: ${e.message}`] }; }
  if (data && !Array.isArray(data) && Array.isArray(data.records)) data = data.records;
  if (!Array.isArray(data)) return { rows: [], errors: ['Expected a JSON array of records'] };
  const rows = data.map((item, i) => {
    const problems = [];
    const it = item && typeof item === 'object' ? item : {};
    const emp = findEmployee(it, employees);
    if (!emp) problems.push(`unknown employee "${it.employee ?? it.name ?? it.employeeID ?? ''}"`);
    const workday = normDate(it.date ?? it.workday ?? it.day);
    if (!workday) problems.push('bad date');
    const shift = findShift(it, shifts);
    if (!shift) problems.push(`unknown shift "${it.shift}"`);
    const startTime = normTime(it.start ?? it.startTime ?? it.from);
    const endTime = normTime(it.end ?? it.endTime ?? it.to);
    if (!startTime || !endTime) problems.push('bad start/end time');
    const breakMinutes = Number(it.break ?? it.breakMinutes ?? 0) || 0;
    let hours = null;
    if (startTime && endTime) {
      const [sh, sm] = startTime.split(':').map(Number), [eh, em] = endTime.split(':').map(Number);
      hours = ((eh * 60 + em) - (sh * 60 + sm) - breakMinutes) / 60;
      if (hours <= 0) problems.push('end is not after start');
    }
    return {
      n: i + 1, employeeID: emp ? emp.employeeID : null, employeeName: emp ? nameOf(emp) : String(it.employee ?? it.name ?? ''),
      workday, shift: shift ? shift.id : null, shiftName: shift ? shift.name : '', startTime, endTime, breakMinutes,
      hours: hours != null && hours > 0 ? Math.round(hours * 100) / 100 : null,
      error: problems.length ? problems.join('; ') : null,
    };
  });
  return { rows, errors: [] };
}
